"use client";
import {
  AgendaDay as AgendaDayType,
  IWorkingLocationPresenceType,
  WL_PRESENCE_OFFICE
} from "@gettactic/api";
import { getObjectKeys } from "@gettactic/helpers";
import React from "react";
import { AgendaDay } from "@/features/dashboard/components/AgendaDay";
import { useDashboardFilters } from "@/features/dashboard/DashboardStateContext";
import { useDashboardLocation } from "@/features/dashboard/hooks/useDashboardLocation";

interface MobileSelectedDayDetailProps {
  locationData: ReturnType<typeof useDashboardLocation>;
  selectedDayIndex: number;
}

export const MobileSelectedDayDetail: React.FC<
  MobileSelectedDayDetailProps
> = ({ locationData, selectedDayIndex }) => {
  const {
    scheduleMeData,
    officeAvailabilityData,
    isLoadingOfficeAvailability,
    updateWorkingLocation,
    getLocationForDate,
    isLocationLoading,
    authenticatedUser,
    dedicatedDesk,
    isDeskReleasedOn,
    areDeskReleasesKnown,
    areDeskReleasesLoading
  } = locationData;

  const days = getObjectKeys(scheduleMeData.data?.days || {});
  const { selectedEvent, setSelectedEvent } = useDashboardFilters();

  const date = days[selectedDayIndex];
  if (!date) return null;

  const todaysAgenda = scheduleMeData.data?.agenda[date] as
    | AgendaDayType
    | undefined;

  if (!todaysAgenda) return null;

  if (todaysAgenda.location === "not_available") return null;

  const todaysOfficeAvailability =
    officeAvailabilityData?.days?.[date]?.[todaysAgenda?.office_id ?? ""] ??
    null;

  const location = todaysAgenda?.location;
  const officeId = todaysAgenda?.office_id;

  const effectiveDay = getLocationForDate(
    date,
    scheduleMeData.data?.agenda[date] || null
  );

  const isLoading = isLocationLoading(date);

  return (
    <div className="mx-4 rounded-2xl border border-neutral-100 bg-white p-3 shadow-sm">
      <AgendaDay
        key={`mobile_day_${date}_${location}`}
        office_id={officeId}
        date={date}
        location={
          location !== WL_PRESENCE_OFFICE
            ? (location as IWorkingLocationPresenceType)
            : null
        }
        workingLocationId={officeId ?? undefined}
        schedule={scheduleMeData.data?.agenda[date]?.schedules ?? []}
        onSelectItemChange={setSelectedEvent}
        selectedEvent={selectedEvent}
        day={todaysAgenda || null}
        isLoadingOfficeAvailability={isLoadingOfficeAvailability}
        officeAvailability={todaysOfficeAvailability}
        onLocationChange={(newLocation) => {
          updateWorkingLocation(
            newLocation,
            date,
            selectedDayIndex,
            todaysAgenda
          );
        }}
        isLocationLoading={isLoading}
        optimisticLocation={
          effectiveDay &&
          todaysAgenda &&
          effectiveDay.location !== todaysAgenda.location
            ? effectiveDay
            : effectiveDay?.office_id !== todaysAgenda.office_id
              ? effectiveDay
              : null
        }
        isMobile
        orgSlug={authenticatedUser.slug ?? undefined}
        dedicatedDesk={dedicatedDesk}
        isDeskReleased={isDeskReleasedOn(date)}
        areDeskReleasesKnown={areDeskReleasesKnown}
        areDeskReleasesLoading={areDeskReleasesLoading}
      />
    </div>
  );
};
